import ReactECharts from 'echarts-for-react';
import type { StyleConfig, DateGranularity } from '../../../../types/bi';
import { useEChartsTheme } from '../../hooks/useEChartsTheme';
import { formatCategories } from '../../utils/dateFormat';
import { buildChartClickHandler } from '../../utils/drilldown';
import type { DrilldownEvent } from '../../utils/drilldown';

interface BarChartProps {
  data: { columns: string[]; rows: Record<string, unknown>[] };
  dimensions: string[];
  measures: { alias: string }[];
  style?: StyleConfig;
  horizontal?: boolean;
  stacked?: boolean;
  dateGranularity?: DateGranularity;
  onDrillDown?: (event: DrilldownEvent) => void;
}

function barLabelFormatter(content?: StyleConfig['label_content']): string {
  switch (content) {
    case 'name':
      return '{b}';
    case 'percentage':
      return '{d}%';
    case 'name_value':
      return '{b}: {c}';
    case 'name_pct':
      return '{b} ({d}%)';
    case 'value_pct':
      return '{c} ({d}%)';
    case 'all':
      return '{b}: {c} ({d}%)';
    case 'value':
    default:
      return '{c}';
  }
}

function legendPlacement(pos?: StyleConfig['legend_position'], fallbackShow = true) {
  if (pos === 'none') return { show: false };
  const orient = pos === 'left' || pos === 'right' ? 'vertical' : 'horizontal';
  const placement: Record<string, unknown> = { show: fallbackShow, orient };
  if (pos === 'top') placement.top = 0;
  else if (pos === 'left') {
    placement.left = 0;
    placement.top = 'center';
  } else if (pos === 'right') {
    placement.right = 0;
    placement.top = 'center';
  } else placement.bottom = 0;
  return placement;
}

export default function BarChart({
  data,
  dimensions,
  measures,
  style,
  horizontal = false,
  stacked = false,
  dateGranularity,
  onDrillDown,
}: BarChartProps) {
  const theme = useEChartsTheme();

  const dimKey = dimensions[0] || data.columns[0];
  const categories = formatCategories(
    data.rows.map((r) => String(r[dimKey] ?? '')),
    dateGranularity,
  );

  const showLabels = style?.show_data_labels ?? false;
  const labelPos = style?.label_position || 'top';
  const labelFmt = barLabelFormatter(style?.label_content);
  const showGrid = style?.show_grid_lines ?? true;

  const series = measures.map((m, i) => ({
    name: m.alias,
    type: 'bar' as const,
    data: data.rows.map((r) => Number(r[m.alias]) || 0),
    stack: stacked ? 'total' : undefined,
    itemStyle: style?.colours?.[i] ? { color: style.colours[i] } : undefined,
    label: { show: showLabels, position: labelPos, formatter: labelFmt },
  }));

  const valAxis: Record<string, unknown> = {
    type: 'value' as const,
    name: horizontal ? style?.axis_labels?.x : style?.axis_labels?.y,
    splitLine: { show: showGrid },
  };
  const catAxis: Record<string, unknown> = {
    type: 'category' as const,
    data: categories,
    name: horizontal ? style?.axis_labels?.y : style?.axis_labels?.x,
  };

  const option: Record<string, unknown> = {
    tooltip:
      style?.tooltip_trigger === 'none'
        ? { show: false }
        : { trigger: (style?.tooltip_trigger || 'axis') as string },
    legend: legendPlacement(style?.legend_position, measures.length > 1),
    grid: {
      left: '3%',
      right: '4%',
      bottom: style?.enable_data_zoom ? '15%' : '3%',
      containLabel: true,
    },
    xAxis: horizontal ? valAxis : catAxis,
    yAxis: horizontal ? catAxis : valAxis,
    series,
  };

  if (style?.enable_data_zoom) {
    option.dataZoom = [
      { type: 'slider', show: true, start: 0, end: 100 },
      { type: 'inside', start: 0, end: 100 },
    ];
  }

  const events = buildChartClickHandler(dimensions, onDrillDown);

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      onEvents={events}
    />
  );
}
